import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CanvasStatus {
  connected: boolean;
  instanceUrl: string | null;
  canvasUserId: string | null;
  canvasUserName: string | null;
  connectedAt: string | null;
}

export interface CanvasCourse {
  id: number;
  name: string;
  courseCode?: string;
  term?: string;
  startAt?: string;
  endAt?: string;
  enrollmentRole?: string;
  enrollments?: Array<{ role?: string; type?: string }>;
}

export interface CanvasPage {
  url: string;
  title: string;
  updatedAt?: string;
  htmlUrl?: string;
  selected?: boolean;
}

export interface CanvasFile {
  id: number;
  displayName: string;
  url: string;
  previewUrl?: string;
  thumbnailUrl?: string | null;
  contentType?: string;
  size?: number;
  updatedAt?: string;
  mediaType?: 'Video' | 'PDF' | 'Photo' | 'Audio' | 'Document' | null;
  supported?: boolean;
  selected?: boolean;
}

export interface CanvasImportPayload {
  courseId: number;
  courseName?: string;
  canvasRole?: string;
  tangramRole?: 'STUDENT' | 'TA' | 'CURATOR';
  pages?: Array<{ url: string; title: string }>;
  files?: Array<{ url: string; displayName: string; contentType?: string }>;
}

@Injectable({ providedIn: 'root' })
export class CanvasService {
  private readonly baseUrl = `${environment.baseUrl}/canvas`;

  constructor(private http: HttpClient) {}

  getStatus(): Observable<{ success: boolean; data: CanvasStatus }> {
    return this.http.get<{ success: boolean; data: CanvasStatus }>(`${this.baseUrl}/status`);
  }

  connect(instanceUrl: string, accessToken: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/connect`, { instanceUrl, accessToken });
  }

  disconnect(): Observable<any> {
    return this.http.post(`${this.baseUrl}/disconnect`, {});
  }

  listCourses(): Observable<{ success: boolean; data: CanvasCourse[] }> {
    return this.http.get<{ success: boolean; data: CanvasCourse[] }>(`${this.baseUrl}/courses`).pipe(
      tap((res) => console.log('[Canvas] courses:', res && res.data)),
    );
  }

  listPages(courseId: number): Observable<{ success: boolean; data: CanvasPage[] }> {
    return this.http.get<{ success: boolean; data: CanvasPage[] }>(`${this.baseUrl}/courses/${courseId}/pages`).pipe(
      tap((res) => console.log('[Canvas] pages:', res && res.data)),
    );
  }

  listFiles(courseId: number): Observable<{ success: boolean; data: CanvasFile[] }> {
    return this.http.get<{ success: boolean; data: CanvasFile[] }>(`${this.baseUrl}/courses/${courseId}/files`).pipe(
      tap((res) => console.log('[Canvas] files:', res && res.data)),
    );
  }

  importCourse(payload: CanvasImportPayload): Observable<any> {
    console.log('[Canvas] import payload:', payload);
    return this.http.post(`${this.baseUrl}/import`, payload).pipe(
      tap((res) => console.log('[Canvas] import response:', res)),
    );
  }
}
